import { parsePositionNumber } from '../cmm-import/parsePositionNumber.ts';
import { xlsxFileToTable } from '../cmm-import/xlsxFileToTable.ts';

export interface ZeissFileMetadata {
    partNo: string;
    date: string;
    time: string;
}

export interface ZeissPositionRead {
    positionName: string;
    actual: string;
    nominal: string;
    upperTol: string;
    lowerTol: string;
    deviation: string;
}

export type ZeissFileReadResult =
    | { success: false; errorMessage: string }
    | {
        success: true;
        metadata: ZeissFileMetadata;
        positions: ZeissPositionRead[];
        skippedInvalidPositions: number;
        skippedInvalidPositionNames: string[];
    };

// Zeiss Calypso report structure (1-indexed Excel → 0-indexed below):
//   Row 4  (index 3): labels — B="Measurement Plan", F="Order"
//   Row 5  (index 4): values — D=date, F=order number
//   Row 7  (index 6): labels — F="Part No."
//   Row 8  (index 7): values — D=time, F=part number
//   Row 14 (index 13): first data row — A=position name, B=actual, C=nominal,
//                                        D=upper tol, E=lower tol, F=deviation
const FIRST_DATA_ROW = 13;

export async function readZeissCmmFile(file: File): Promise<ZeissFileReadResult> {
    let table: string[][];
    try {
        table = await xlsxFileToTable(file);
    } catch (error: any) {
        return { success: false, errorMessage: `Error reading file "${file.name}": ${error.message}` };
    }

    // Validate Zeiss Calypso format by checking label cells
    if (getCell(table, 3, 1) !== 'Measurement Plan') {
        return { success: false, errorMessage: `"${file.name}" is not a Zeiss Calypso report (Measurement Plan label not found).` };
    }
    if (getCell(table, 3, 5) !== 'Order') {
        return { success: false, errorMessage: `"${file.name}" is not a Zeiss Calypso report (Order label not found).` };
    }
    if (getCell(table, 6, 5) !== 'Part No.') {
        return { success: false, errorMessage: `"${file.name}" is not a Zeiss Calypso report (Part No. label not found).` };
    }

    const metadata: ZeissFileMetadata = {
        date: formatExcelDate(getCell(table, 4, 3)),
        time: formatExcelTime(getCell(table, 7, 3)),
        partNo: getCell(table, 7, 5),
    };

    const positions: ZeissPositionRead[] = [];
    let skippedInvalidPositions = 0;
    const skippedInvalidPositionNames: string[] = [];
    for (const row of table.slice(FIRST_DATA_ROW)) {
        const name = row[0]?.trim() ?? '';
        if (!name) continue;
        // Skip positions with invalid position numbers (e.g., totals, header repetitions)
        if (parsePositionNumber(name) === null) {
            skippedInvalidPositions += 1;
            skippedInvalidPositionNames.push(name);
            continue;
        }

        positions.push({
            positionName: name,
            actual: row[1]?.trim() ?? '',
            nominal: row[2]?.trim() ?? '',
            upperTol: row[3]?.trim() ?? '',
            lowerTol: row[4]?.trim() ?? '',
            deviation: row[5]?.trim() ?? '',
        });
    }

    return { success: true, metadata, positions, skippedInvalidPositions, skippedInvalidPositionNames };
}

/** Returns trimmed cell value using 0-based row and column indices. */
function getCell(table: string[][], row: number, col: number): string {
    return table[row]?.[col]?.trim() ?? '';
}

/**
 * Converts an Excel serial date number (e.g. 46153) to a YYYY-MM-DD string.
 * If the value is not a valid serial, returns it unchanged.
 */
function formatExcelDate(value: string): string {
    const num = Number(value);
    if (!Number.isFinite(num) || num < 1) return value;
    const date = new Date(Math.round((num - 25569) * 86400000));
    const y = date.getUTCFullYear();
    const m = String(date.getUTCMonth() + 1).padStart(2, '0');
    const d = String(date.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}

/**
 * Converts an Excel serial time fraction (e.g. 0.432...) to a HH:MM:SS string.
 * If the value is not a fractional number in [0, 1), returns it unchanged.
 */
function formatExcelTime(value: string): string {
    const num = Number(value);
    if (!Number.isFinite(num) || num < 0 || num >= 1) return value;
    const totalSeconds = Math.round(num * 86400);
    const h = String(Math.floor(totalSeconds / 3600)).padStart(2, '0');
    const min = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, '0');
    const s = String(totalSeconds % 60).padStart(2, '0');
    return `${h}:${min}:${s}`;
}
